import {useRef, createContext, useContext, useState} from "react";
import {pickIndex, localStorageSetItem,localStorageGetItem, getRepeatFactor} from "./misc";
import {useHash} from "./HashContext"


import {useServer} from "./ServerContext"

/*
 * SCORING/WEIGHTS/PROBAS for each flatText we have a pair [success,failure] = [s,f]
 *
 * weight(flatText) = s-f/failureFactor
 * weight(word) = min weight(forms)
 *
 * PROBAS(text) : (factor*n)**(-weight)  (n= number of texts/forms, factor = 2 ?)
 *
 * Score(flatText) = weight
 * Score(word) =
 *          - we set k = int(min(|scores of the forms|))
 *          - score = k + average(capped(scores of the forms -k))
 *
 * Score(exercise) : on flatTexts = average of all the max(min(weight/repeatFactor, 1),-1) --> score between -1 and 1. It should be 1 one all the text scores > 1
 *
 * repeatFactor : indicates how many repetition before reaching 100%
 */

const StatsContext = createContext(null);

export function StatsProvider({children}) {

    const failureFactor = 2;
    const historySize = 17;
    const probaFactor = 3;
    const repeatFactor = getRepeatFactor();

    const {ServerEvents, addEvent2ServerQueue} = useServer();
    const {isHash, hash} = useHash();

//
// Low-level Functions for Managing the stats useRef
//

    const useRefStats = useRef({});

    // Compute the two keys :
    //     - statsRefKey1 : is the name of key used to store information and the filename. It stays intern to this file
    //     - statsRefKey2 : is the name that is used externally
    //
    const getStatsRefKeys = (textType, mode) => {
        let statsRefKey1 = null;
        let statsRefKey2 = null;
        if (textType === "words" || textType === "sentences")
            statsRefKey1 = textType + "Stats"
        else
            statsRefKey1 = textType + "Stats" + mode;
        statsRefKey2 = textType + "Stats" + mode;

        return [statsRefKey1, statsRefKey2];
    }

    const getTextTypeModeFromStatsRefKey = (statsRefKey) => {
        if (statsRefKey.length >= "wordsStats".length && statsRefKey.slice(0, "wordsStats".length) === "wordsStats")
            return (["words", statsRefKey.slice("wordsStats".length)]);
        if (statsRefKey.length >= "sentencesStats".length && statsRefKey.slice(0, "sentencesStats".length) === "sentencesStats")
            return (["sentences", statsRefKey.slice("sentencesStats".length)]);
        else
            return [statsRefKey, null] /// ??? BOF
    }

    const useStatsUseRef = (textType, mode) => {

        const statsRef = useRef(null);
        const [statsRefKey1, statsRefKey2] = getStatsRefKeys(textType, mode);

        if (statsRefKey1 in useRefStats.current) return statsRefKey2;

        statsRef.current = {values: JSON.parse(localStorageGetItem(statsRefKey1)) || {}, flagUpdate: false}

        useRefStats.current[statsRefKey1] = {
            textType,
            mode,
            statsRef,
            filename: statsRefKey1
        }
        return statsRefKey2;
    }

    // Hashing the exData
    const exDataHash = (exData) => {
        return isHash() ? hash(exData[3]) : exData[3]
    }

    // Get the list value of an exData  (a list value is a 2 dimensional array [success, failure])
    const getStatsRefListValue = (statsRefKey, exData) => {
        const exDataStr = exDataHash(exData);
        const [textType, mode] = getTextTypeModeFromStatsRefKey(statsRefKey)
        if (textType === "words") {
/*            console.log("PROBAS",exData,
                (useRefStats.current["wordsStats"].statsRef.current.values?.[exDataStr] ?? {})?.[mode] ?? [0, 0],
                listValue2Proba((useRefStats.current["wordsStats"].statsRef.current.values?.[exDataStr] ?? {})?.[mode] ?? [0, 0]))
*/
            return (useRefStats.current["wordsStats"].statsRef.current.values?.[exDataStr] ?? {})?.[mode] ?? [0, 0];
        }
        // Case of sentencesStats
        if (textType === "sentences") {
//            console.log("** getStatsRefWeight("+statsRefKey+","+exDataStr+") --> ",
//                (useRefStats.current["sentencesStats"].statsRef.current?.[exDataStr] ?? {})?.[mode] ??  [0,0])
//            console.log("useRefStats = ", useRefStats.current)
            return (useRefStats.current["sentencesStats"].statsRef.current.values?.[exDataStr] ?? {})?.[mode] ?? [0, 0];
        }
        // other cases
        return useRefStats.current[statsRefKey].statsRef.current.values?.[exDataStr] ?? [0, 0]
    }

    // Store an exData list value (a list value is a 2 dimensional array [success, failure])
    const setStatsRefListValue = (statsRefKey, exData, value) => {
        const exDataStr = exDataHash(exData);
        const [textType, mode] = getTextTypeModeFromStatsRefKey(statsRefKey);

        if (textType === "words") {
            if (!(exDataStr in useRefStats.current["wordsStats"].statsRef.current.values)) {
                useRefStats.current["wordsStats"].statsRef.current.values[exDataStr] = {};
            }
            useRefStats.current["wordsStats"].statsRef.current.values[exDataStr][mode] = value;
            useRefStats.current["wordsStats"].statsRef.current.flagUpdate = true;
        } else if (textType === "sentences") {
            if (!(exDataStr in useRefStats.current["sentencesStats"].statsRef.current.values)) {
                useRefStats.current["sentencesStats"].statsRef.current.values[exDataStr] = {};
            }
            useRefStats.current["sentencesStats"].statsRef.current.values[exDataStr][mode] = value;
            useRefStats.current["sentencesStats"].statsRef.current.flagUpdate = true;
        } else {
            useRefStats.current[statsRefKey].statsRef.current.values[exDataStr] = value;
            useRefStats.current[statsRefKey].statsRef.current.flagUpdate = true;
        }
//        console.log("** setStatsRefListValue("+statsRefKey+","+exData+",["+ value[0]+","+value[1]+"])")
//        console.log("useRefStats = ", useRefStats.current)
    }


    const addSaveStatsServerTaskIfNeeded = () => {
        let mustSaveOnServer = false;
        Object.keys(useRefStats.current).forEach(statsRefKey => {
            if (useRefStats.current[statsRefKey].statsRef.current.flagUpdate) {
                mustSaveOnServer = true;
                localStorageSetItem(useRefStats.current[statsRefKey].filename,
                    JSON.stringify(useRefStats.current[statsRefKey].statsRef.current.values))
            }
        })
        if (mustSaveOnServer) {
            addEvent2ServerQueue("updateStats");
        }
    }

    const onLeaveStatsContext = () => {
        const [statsRefKey1, ] = getStatsRefKeys("courses","learnt")

        addSaveStatsServerTaskIfNeeded();
        Object.keys(useRefStats.current).forEach(statsRefKey => {
                delete useRefStats.current[statsRefKey].statsRef;
                delete useRefStats.current[statsRefKey];
            }
        )
        statsCache.current = null;
        pickupHistory.current = [];
    }

    //
    // High level functions
    //
    const markExDataAsSuccess = (statsRefKey, exData) => {
        const [success, failure] = getStatsRefListValue(statsRefKey, exData);
        setStatsRefListValue(statsRefKey, exData, [success + 1, failure]);
    }

    const markExDataAsFailure = (statsRefKey, exData) => {
        const [success, failure] = getStatsRefListValue(statsRefKey, exData);
        setStatsRefListValue(statsRefKey, exData, [success, failure + 1]);
    }


    //
    // Removing stats
    //
    const removeTextsFromStats = (textType, mode, texts) => {
        if (texts.length === 0) return
        let flagUpdate = false
        if (textType === "versions" || textType === "themes") textType = 'sentences';
        const [statsRefKeys,] = getStatsRefKeys(textType, mode);
        let stats = JSON.parse(localStorage.getItem(statsRefKeys) || "{}");
        console.log("texts to be removed", texts)
        if (stats.length === 0) return;

        let texts1
        if (texts[0].type === 'words') {
            texts1 = texts.flatMap(t => Object.values(t.formes))
        }
        else if (texts[0].type === 'sentences') {
            texts1 = texts.map(t => t.exData)
        }
        else {
            texts1 = texts.flatMap(t => t.exDataList)
        }
        texts1 = texts1.map(t => exDataHash(t))

        for (let text1 of texts1) {
            if (text1 in stats && (mode in stats[text1])) {
                console.log("delete ", text1,mode)
                delete stats[text1][mode]
                if (Object.keys(stats[text1]).length === 0) delete stats[text1]
                flagUpdate = true
            }
        }

        if (flagUpdate) {
            localStorage.setItem(statsRefKeys, JSON.stringify(stats))
            addEvent2ServerQueue("overrideServerStats")
        }
        statsCache.current = null;
    }



    //
    // Managing stats/probas
    //


    // Gets weight from ListValue [success, failure] of flatText : success-failure/2.0
    const listValue2Weight = (listValue) => {
        return listValue[0]-listValue[1]/failureFactor;
    }

    // Gets weight from ListValues of words
    const wordListValues2Weight = (listValues) => {
        return listValues.reduce((acc,lv) => Math.min(listValue2Weight(lv),acc), 10000);
    }

    // Gets weight of a text from ListValues of sentences
    const textListValues2Weight = (listValues) => {
        return listValues.reduce((acc,lv) => Math.min(listValue2Weight(lv),acc), 10000);
    }

    const weight2Proba = (weight, nTexts) => {
        return (probaFactor*nTexts)**(-weight);
    }

    // Gets proba from ListValue
    const listValue2Proba = (listValue,nTexts) => {
        return weight2Proba(listValue2Weight(listValue),nTexts);
    }

    // Normalize an array of stats to become a proba
    const normalizeProbas = (stats) => {
        const sum = stats.reduce((acc, val) => acc + val, 0);
        return (sum === 0 ? stats.map((p) => 1. / stats.length) : stats.map((p) => p / sum));
    }

    // This is the history of the pickup used to forbid failure text to be selected
    const pickupHistory = useRef([])

    // pickup an index according to unormalized probas and pickupHistory
    const pickupFromNotNormalizedProba = (probasNotNormalized, weights, textList) => {
        // We first manage the history
        // A priori we set all the probas of texts in history to 0
        // However, if
        // - there is no texts left with a weight < 1 outside of history
        // - there are some texts with a weight < 1 inside the history
        // - if the eldest text in history that has a weight < 1 has a rank > rollingMin
        // We take it out of the history so that it can be selected !

        // HISTORY UPDATE
        // In the following we first deal with this condition
        const smallWeightIndices = []; // the indices (in textsList) of the small weight texts
        let nSmallWeightIndicesInHistory = 0;
        let lastSmallWeightHistoryIndex = -1;
        weights.forEach((weight, index) => {
            if (weight < 1) {
                smallWeightIndices.push(index);
                if (pickupHistory.current.includes(textList[index])) {
                    nSmallWeightIndicesInHistory += 1;
                    const index1 = pickupHistory.current.indexOf(textList[index])
                    lastSmallWeightHistoryIndex = Math.max(lastSmallWeightHistoryIndex, index1);
                }
            }
        })

        /* START DEBUG */
        /*
        console.log("*** HISTORY(size=" + pickupHistory.current.reduce((acc, w) => acc + Object.keys(w.formes).length, 0).toString() + ") : ")
        pickupHistory.current.forEach((w, n) => {
            let index = -1;
            textList.forEach((w1, index1) => {
                if (Object.values(w.formes)[0][3] === Object.values(w1.formes)[0][3]) index = index1;
            })
            if (index !== -1) {
                console.log("   (HISTORY)", n, " : ", Object.values(w.formes)[0][3],
                    "(" + (Object.values(w.formes).length).toString() + " words)",
                    "weights=", weights[index])
            }
        })
        console.log("   HISTORY : number of small weights out of history : ", smallWeightIndices.length - nSmallWeightIndicesInHistory)
        smallWeightIndices.forEach(index => {
                const w = textList[index];
                let isInHistory = false;
                pickupHistory.current.forEach(w1 => {
                    if (Object.values(w.formes)[0][3] === Object.values(w1.formes)[0][3]) isInHistory = true;
                })
                if (!isInHistory) {
                    console.log("   --> HISTORY ", Object.values(w.formes)[0][3], weights[index])
                }
            }
        )*/
        /* STOP DEBUG */

        // Test condition to remove last small weight text in history
        // If we need to remove a word with a small score from the history ... we have to wait it
        // has a position > rollingMin.
        const rollingMin = 2;
        if (smallWeightIndices.length !== 0 &&
            smallWeightIndices.length === nSmallWeightIndicesInHistory &&
            lastSmallWeightHistoryIndex > rollingMin) {
            console.log("HISTORY REMOVE historyIndex=",pickupHistory.current[lastSmallWeightHistoryIndex])
            pickupHistory.current.splice(lastSmallWeightHistoryIndex, 1)
        }

        // NOW we can set the probas of small weight texts to 0 !
        const pickupHistorySize = historySize;
        if (pickupHistorySize!==0) {
            pickupHistory.current = pickupHistory.current.slice(0,Math.min(pickupHistorySize,pickupHistory.current.length))
            pickupHistory.current.forEach(text => {
                const index = textList.indexOf(text);
                if (index >= 0) probasNotNormalized[index] = 0
            });
        }

        // Then we normalize the probas
        const probas = normalizeProbas(probasNotNormalized);

   //     textList.forEach((t,index) => console.log("PROBAS", t.formes[Object.keys(t.formes)[0]],probas[index]))

        // We pickup the index
        let index = pickIndex(probas)

        // We update the pickupHistory
        // Case for words
        if (textList[0].type === "words") {
            const size2Add = Object.keys(textList[index].formes).length;
            let totalSize = pickupHistory.current.reduce((acc,w) => acc+ Object.keys(w.formes).length,0);
            while (totalSize >= pickupHistorySize-size2Add && totalSize > 3) {
                const w = pickupHistory.current.pop();
                totalSize -= Object.keys(w.formes).length;
            }
        }
        else {
            if (pickupHistory.current.length >= pickupHistorySize)
                pickupHistory.current.pop();
        }
        pickupHistory.current.unshift(textList[index])

        console.log("PROBAS history",pickupHistory.current,pickupHistorySize)
        console.log("PROBAS : ", probas)

        return index;
    }

    //
    // Compute the probas associated to a list of ExData of the form :
    //     if field is null : list of Arrays or
    //     otherwise        : list of {field: exData, ....}
    //
    const computeExDataListProbas = (statsRefKey, exDataList, field) => {
        return normalizeProbas(exDataList.map((obj) =>
            listValue2Proba(getStatsRefListValue(statsRefKey, field ? obj[field] : obj),exDataList.length)));
    }
    const computeExDataListWeights = (statsRefKey, exDataList, field) => {
        return exDataList.map((obj) =>
            listValue2Weight(getStatsRefListValue(statsRefKey, field ? obj[field] : obj),exDataList.length));
    }

    // Pickup a word and a hebrew form in a list of words according to the probas of statsRefKey
    const pickupRandomWordAndForm = (statsRefKey, wordList) => {

        // We first compute the words weight
        const weights = wordList.map(word =>
            wordListValues2Weight(Object.values(word.formes).map(exData => getStatsRefListValue(statsRefKey, exData)))
        );

        // then the unormalized probas for the words
        const probasNotNormalized = weights.map(w => weight2Proba(w,wordList.length));

        console.log("PROBAS", wordList)

        // We get the index
        let index = pickupFromNotNormalizedProba(probasNotNormalized,weights,wordList)
        const word = wordList[index];

        // Then picking up the form
        index = 0;
        if (Object.keys(word.formes).length > 1) {
            const probas = computeExDataListProbas(statsRefKey, Object.values(word.formes), null);
            index = pickIndex(probas);
        }
        const hebrewForm = Object.keys(word.formes)[index];

        return [word, hebrewForm]
//                        flatText = TextForm2FlatText(text, hebrewForm);
    }

    // Pickup an exData from an exDataList according to the probas of this exDataList
    // This is used for sentences !
    const pickupRandomExData = (statsRefKey, exDataList, field) => {
        const weights = computeExDataListWeights(statsRefKey, exDataList, field);
        const probas = weights.map(w => weight2Proba(w,exDataList.length))

        const index = pickupFromNotNormalizedProba(probas,weights,exDataList)

        console.log("PROBAS : ", exDataList)
//                   texts.forEach((t,index) => console.log(t,probas[index]))
        return field ? exDataList[index][field] : exDataList[index];
    }

    // Pickup a random text (versions or themes) : History does not have any impact
    // statsRefKey : corresponds to sentences
    const pickupRandomText = (statsRefKey, textList) => {
        if (textList.length === 1) return textList[0];
        const weights = textList.map(text => textListValues2Weight(text.exDataList.map(exData => getStatsRefListValue(statsRefKey, exData))))

        const probas = weights.map(w => weight2Proba(w,textList.length))
        console.log("PROBAS : ", textList)

        const index = pickupFromNotNormalizedProba(probas,weights,textList)

        return textList[index];
    }

    //
    // Managing scores
    //

    // A cache useRef for the last stats that has been used
    // if it is not already in a statsRefKey useRef.
    const statsCache = useRef(null);
    const userCache = useRef(null)
    function getFlatTextScore(textType, mode, flatText) {

        if (localStorage.getItem("currentUsername") !== userCache.current) {
            console.log("Bingo cache null", localStorage.getItem("currentUsername"),userCache.current)
            statsCache.current = null
            userCache.current = localStorage.getItem("currentUsername");
        }

        // In case flatText is a version or a theme, it is a list of sentences ...
        // The score is just given using getExScore
        if (textType==="versions" || textType==="themes") {
            return flatText.reduce((acc, ft) => acc + repeatFactor*Math.max(-1, Math.min(1, getFlatTextScore("sentences", mode, ft)/repeatFactor)), 0) / flatText.length
            return getExScore("sentences", mode, flatText, 1.0)
        }

        const textType1 = textType;
        const [statsRefKey1, ] = getStatsRefKeys(textType1,mode)

        //
        // Get the statsValues
        //
        let statsValues = null

        // Case the stats are already stored in a statsRefKey useRef already
         if (useRefStats.current?.[statsRefKey1]?.statsRef) {
             statsValues = useRefStats.current[statsRefKey1].statsRef.current.values
         }
        // Otherwise we test the cache
        else if (statsCache.current && statsCache.current.statsRefKey1 === statsRefKey1) {
            statsValues = statsCache.current.values;
         }
        // Otherwise we load the cache
        else {
            statsValues = JSON.parse(localStorageGetItem(statsRefKey1)) || {}
            statsCache.current = {statsRefKey1: statsRefKey1, values: statsValues}
        }

        // Extract the score of the flatText
        const listValue = (textType !== "words" && textType !== "sentences"
            ? statsValues?.[exDataHash(flatText)]
            : statsValues?.[exDataHash(flatText)]?.[mode]
        ) ?? [0, 0];

        return listValue2Weight(listValue);
    }


    // Returns a list of
    // - the score of the word
    // - a dictionnary : {form: score of the form}
    function getWordScores(word, mode) {
        const formsScores = Object.keys(word.formes).reduce((acc,f) => {
            const score = getFlatTextScore("words", mode, word.formes[f]);
            acc[f] = score;
            return acc
        }, {})
        const min = Math.trunc(Object.values(formsScores).reduce((acc,s) => Math.min(acc,s),10000));
        const cappedFormsScores = Object.values(formsScores).map(s => min >= 0
            ? (s >= min+1 ? min+1 : s)
            : (s <= min-1 ? min-1 : s)
        )
        return [min + cappedFormsScores.reduce((acc,s) => acc+s-min,0)/Object.keys(word.formes).length, formsScores];
    }

    function getExScore(textType, mode, flatTexts, rFactor=-1) {
        if (flatTexts.length === 0) return 0;
        if (rFactor < 0) rFactor = repeatFactor;
        flatTexts.map(ft => {
            let score = getFlatTextScore(textType,mode,ft)/rFactor/flatTexts.length
            return 1
        })
        const s =  flatTexts.reduce((acc,ft) => acc+Math.max(-1,Math.min(1, getFlatTextScore(textType,mode,ft)/rFactor)),0)/flatTexts.length
        return s;

    }

    return (
        <StatsContext.Provider value={{
            getFlatTextScore,
            getExScore,
            getWordScores,
            listValue2Weight,
            computeExDataListProbas,
            markExDataAsSuccess,
            markExDataAsFailure,
            useStatsUseRef,
            onLeaveStatsContext,
            pickupRandomWordAndForm,
            pickupRandomExData,
            pickupRandomText,
            removeTextsFromStats
        }}>
            {children}
        </StatsContext.Provider>
    );
}

export function useStats() {
    return useContext(StatsContext);
}
